// src/hooks/useMetaWatcher.js — REFAKTORIERT
//
// KRITISCHER BUG DER ALTEN VERSION (Feature war komplett tot):
// Der Callback war als `onChange: (changed, meta) => {...}` deklariert, aber
// startMetaWatcher() ruft ihn mit nur EINEM Argument auf: `onChange(changed)`.
// `meta` war also immer `undefined` →
//     metaTeams = safeNum(undefined) = 0
//     lastMeta  = { teams: 0, stations: 0, results: 0 }
//     almostEqual(0, 0) = true  →  teamsChanged = false (für alle drei)
// Damit wurde NIE ein Listener benachrichtigt. Die automatische Aktualisierung
// zwischen Betreuer-Tablets hat in der ganzen Veranstaltung nie funktioniert –
// still, ohne Fehlermeldung.
//
// Zweiter Fehler: `almostEqual(a,b) => Math.abs(a-b) < 5` auf Millisekunden-
// Timestamps. Selbst mit korrekten Daten wäre die Toleranz sinnlos gewesen.
//
// FIX: Die Änderungserkennung passiert bereits vollständig in startMetaWatcher().
// Der Hook reicht das Ergebnis nur noch weiter, statt es fehlerhaft nachzubauen.

import { useEffect, useRef } from "react";
import { startMetaWatcher } from "../utils/loadCachedData";

const listeners = new Set();
let globalStopper = null;
let cleanupTimeoutId = null;
let visibilityHandler = null;
let watcherConfig = null; // { backendUrl, navigate } für Reaktivierung nach Tab-Wechsel

function runWatcher() {
  if (globalStopper || !watcherConfig) return;

  globalStopper = startMetaWatcher(
    watcherConfig.backendUrl,
    watcherConfig.navigate,
    {
      // ÄNDERUNG: `changed` direkt durchreichen – keine zweite, kaputte Ableitung mehr.
      onChange: (changed) => {
        if (!changed || (!changed.teams && !changed.stations && !changed.results)) return;

        for (const listener of listeners) {
          try {
            listener(changed);
          } catch (err) {
            console.warn("[MetaWatcher] Listener-Fehler:", err);
          }
        }
      },
    },
    120000
  );
}

function stopWatcher() {
  if (!globalStopper) return;
  try {
    globalStopper();
  } catch {
    /* ignore */
  }
  globalStopper = null;
}

function ensureVisibilityHandling() {
  if (visibilityHandler) return;

  visibilityHandler = () => {
    if (document.visibilityState !== "visible") return;
    if (listeners.size === 0) return;

    // Der Watcher wird beim Tab-Wechsel NICHT gestoppt – er pausiert intern nur
    // seine Netzwerkabfragen (siehe startMetaWatcher). Dadurch bleibt sein
    // Bezugspunkt erhalten, und beim Zurückkommen genügt eine sofortige Prüfung:
    // Änderungen aus der Abwesenheit werden erkannt, ohne bis zur nächsten
    // regulären Abfrage (bis zu 2 Minuten) zu warten – und ohne blinden Reload,
    // falls in der Zwischenzeit nichts passiert ist.
    if (!globalStopper) runWatcher();
    if (typeof globalStopper?.checkNow === "function") globalStopper.checkNow();
  };
  document.addEventListener("visibilitychange", visibilityHandler);
}

export default function useMetaWatcher(backendUrl, navigate, onChange) {
  const callbackRef = useRef(onChange);
  callbackRef.current = onChange; // immer die frischeste Closure, ohne Neu-Registrierung

  useEffect(() => {
    if (!backendUrl) return undefined;

    watcherConfig = { backendUrl, navigate };
    ensureVisibilityHandling();
    runWatcher();

    const listener = (changed) => callbackRef.current?.(changed);
    listeners.add(listener);

    if (cleanupTimeoutId) {
      clearTimeout(cleanupTimeoutId);
      cleanupTimeoutId = null;
    }

    return () => {
      listeners.delete(listener);
      if (listeners.size > 0) return;

      // Kurze Gnadenfrist, damit ein Seitenwechsel den Watcher nicht neu startet.
      cleanupTimeoutId = setTimeout(() => {
        cleanupTimeoutId = null;
        if (listeners.size === 0) stopWatcher();
      }, 10000);
    };
  }, [backendUrl, navigate]);
}
